import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs-timezone-iana-plugin";
import "dayjs/locale/uk";
import { Button, Form, InputGroup } from "react-bootstrap";
import { Lock, CloudArrowDown } from "react-bootstrap-icons";
import Loader from "../components/common/Loader";
import { TIMEZONE } from "../constans";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("uk");

/** IANA-ім'я з constans (у dayjs-timezone-iana-plugin лише Europe/Kiev, не Europe/Kyiv). */
const APP_TZ = TIMEZONE;
const USD_FALLBACK = 40;

/** Поля вводу для злиття з live Snapshot (тимчасовий «чернетка» для поточного місяця лише в RAM). */
const DECOMPOSITION_INPUT_KEYS = [
    "turnoverUah",
    "marginUah",
    "leads",
    "salesTotal",
    "repeatSales",
    "goodsQty",
    "adBudgetUsd",
    "managers",
    "payrollUah",
    "reinvestmentUah",
    "otherUah",
];

function hasOwnKey(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
}

/** Старі ключі локальних правок поточного місяця (більше не використовуються). */
function clearLegacyDecompositionOpenBrowserStorage() {
    const prefixes = ["decomposition:openOverrides:v1:", "decomposition:openLive:v1:"];
    try {
        for (const storage of [sessionStorage, localStorage]) {
            for (let i = storage.length - 1; i >= 0; i--) {
                const k = storage.key(i);
                if (!k) continue;
                if (prefixes.some((p) => k.startsWith(p))) storage.removeItem(k);
            }
        }
    } catch (e) {
        console.error(e);
    }
}

function roundUah(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.round(x * 100) / 100;
}

function mergeLiveWithOverridesClient(liveAgg, overridesRaw) {
    const ov =
        overridesRaw && typeof overridesRaw === "object" && !Array.isArray(overridesRaw) ? overridesRaw : {};
    const inputs = {
        turnoverUah: hasOwnKey(ov, "turnoverUah") ? roundUah(ov.turnoverUah) : liveAgg.turnoverUah,
        marginUah: hasOwnKey(ov, "marginUah") ? roundUah(ov.marginUah) : liveAgg.marginUah,
        leads: hasOwnKey(ov, "leads") ? Math.max(0, Math.round(Number(ov.leads) || 0)) : liveAgg.leads,
        salesTotal: hasOwnKey(ov, "salesTotal")
            ? Math.max(0, Math.round(Number(ov.salesTotal) || 0))
            : liveAgg.salesTotal,
        repeatSales: hasOwnKey(ov, "repeatSales")
            ? Math.max(0, Math.round(Number(ov.repeatSales) || 0))
            : liveAgg.repeatSales,
        goodsQty: hasOwnKey(ov, "goodsQty")
            ? Math.max(0, Math.round(Number(ov.goodsQty) || 0))
            : liveAgg.goodsQty,
        adBudgetUsd: hasOwnKey(ov, "adBudgetUsd")
            ? Math.round(Number(ov.adBudgetUsd) * 100) / 100
            : liveAgg.adBudgetUsd,
        managers: hasOwnKey(ov, "managers")
            ? Math.max(1, Math.round(Number(ov.managers) || 2))
            : liveAgg.managers,
        payrollUah: hasOwnKey(ov, "payrollUah") ? roundUah(ov.payrollUah) : liveAgg.payrollUah,
        reinvestmentUah: hasOwnKey(ov, "reinvestmentUah")
            ? roundUah(ov.reinvestmentUah)
            : liveAgg.reinvestmentUah,
        otherUah: hasOwnKey(ov, "otherUah") ? roundUah(ov.otherUah) : liveAgg.otherUah,
    };
    inputs.repeatSales = Math.min(inputs.repeatSales, inputs.salesTotal);
    return inputs;
}

function formatThousandsInt(n) {
    const x = Math.round(Number(n));
    if (!Number.isFinite(x)) return "0";
    return x.toLocaleString("uk-UA", { maximumFractionDigits: 0 });
}

function parseMoneyInput(s) {
    const normalized = String(s || "")
        .replace(/[\s\u00A0\u202F\u2007]/g, "")
        .replace(",", ".");
    const v = parseFloat(normalized);
    return Number.isFinite(v) ? v : 0;
}

function formatMoneyCell(n, displayCurrency, usdSellRate) {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    const rate = Number(usdSellRate);
    if (displayCurrency === "USD" && rate > 0) {
        const usd = Number(n) / rate;
        return usd.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return Math.round(Number(n)).toLocaleString("uk-UA");
}

function formatPct(n) {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return `${Number(n).toLocaleString("uk-UA", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}%`;
}

function formatRatio(n) {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("uk-UA", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

function formatUsd(n) {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function effectiveAdUah(inputs, meta) {
    const usd = Number(inputs.adBudgetUsd) || 0;
    const baseUsd = Number(meta?.baselineAdUsd) || 0;
    const baseUah = Number(meta?.baselineAdUah) || 0;
    if (baseUsd > 0) return roundUah(baseUah * (usd / baseUsd));
    return roundUah(usd * USD_FALLBACK);
}

/** Маржинальність: маржа / ТО × 100. */
function marginPctFromInputs(inputs) {
    const t = Number(inputs.turnoverUah) || 0;
    if (t <= 0) return null;
    return roundUah(((Number(inputs.marginUah) || 0) / t) * 100);
}

function calcDerived(inputs, adBudgetUahEff) {
    const turnoverUah = inputs.turnoverUah;
    const marginUah = inputs.marginUah;
    const leads = Math.max(0, Number(inputs.leads) || 0);
    const salesTotal = Math.max(0, Number(inputs.salesTotal) || 0);
    const repeatSales = Math.max(0, Number(inputs.repeatSales) || 0);
    const goodsQty = Math.max(0, Number(inputs.goodsQty) || 0);
    const managers = Math.max(0, Number(inputs.managers) || 0);
    const payrollUah = inputs.payrollUah;
    const reinvestmentUah = inputs.reinvestmentUah;
    const otherUah = inputs.otherUah;

    const newSales = Math.max(0, salesTotal - repeatSales);

    const avgCheck = salesTotal > 0 ? roundUah(turnoverUah / salesTotal) : null;
    const marginPerUnit = salesTotal > 0 ? roundUah(marginUah / salesTotal) : null;
    const pctRepeat = salesTotal > 0 ? roundUah((repeatSales / salesTotal) * 100) : null;
    const unitsPerCheck = salesTotal > 0 ? roundUah(goodsQty / salesTotal) : null;
    const conversionPct = leads > 0 ? roundUah((salesTotal / leads) * 100) : null;
    const leadsPerManager = managers > 0 ? roundUah(leads / managers) : null;
    const adUah = Number(adBudgetUahEff) || 0;
    const costPerLead = leads > 0 ? roundUah(adUah / leads) : null;
    const newCustomerCac = newSales > 0 ? roundUah(adUah / newSales) : null;
    const customerCac = salesTotal > 0 ? roundUah(adUah / salesTotal) : null;
    const profitPerSale =
        marginPerUnit != null && customerCac != null ? roundUah(marginPerUnit - customerCac) : null;
    const totalProfit = roundUah(marginUah - adUah);
    const roi = adUah > 0 ? roundUah(marginUah / adUah) : null;
    const netPocket = roundUah(
        totalProfit -
            (Number(payrollUah) || 0) -
            (Number(reinvestmentUah) || 0) -
            (Number(otherUah) || 0)
    );

    return {
        avgCheckUah: avgCheck,
        marginPctOfTurnover: marginPctFromInputs(inputs),
        marginPerUnitUah: marginPerUnit,
        pctRepeat,
        unitsPerCheck,
        conversionPct,
        leadsPerManager,
        costPerLeadUah: costPerLead,
        newCustomerCacUah: newCustomerCac,
        customerCacUah: customerCac,
        profitPerSaleUah: profitPerSale,
        totalProfitUah: totalProfit,
        roi,
        netPocketUah: netPocket,
    };
}

function buildOpenMonthClientView(ym, live, fieldOverrides) {
    const inputs = mergeLiveWithOverridesClient(live, fieldOverrides);
    let adUahForFormula;
    if (live.adBudgetUsd > 0) {
        adUahForFormula = roundUah(live.adBudgetUah * (inputs.adBudgetUsd / live.adBudgetUsd));
    } else {
        adUahForFormula = roundUah((inputs.adBudgetUsd || 0) * USD_FALLBACK);
    }
    const calculated = calcDerived(inputs, adUahForFormula);
    const overriddenFields = DECOMPOSITION_INPUT_KEYS.filter((k) =>
        hasOwnKey(fieldOverrides || {}, k)
    );
    return {
        yearMonth: ym,
        saved: false,
        openMonth: true,
        fromSummary: true,
        overriddenFields,
        inputs,
        liveSnapshot: live,
        meta: {
            baselineAdUah: adUahForFormula,
            baselineAdUsd: inputs.adBudgetUsd,
        },
        calculated,
    };
}

function MoneyCellInput({ valueUah, displayCurrency, usdSellRate, disabled, title, showLiveIcon, onCommit }) {
    const rate = Number(usdSellRate) > 0 ? Number(usdSellRate) : USD_FALLBACK;
    const isUsd = displayCurrency === "USD";

    const [local, setLocal] = useState(() => formatThousandsInt(valueUah));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (focused) return;
        if (isUsd) {
            const v = Number(valueUah) / rate;
            setLocal(
                Number.isFinite(v)
                    ? v.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : "0"
            );
        } else {
            setLocal(formatThousandsInt(valueUah));
        }
    }, [valueUah, focused, isUsd, rate, displayCurrency]);

    return (
        <div className="position-relative w-100">
            {showLiveIcon ? (
                <span
                    className="position-absolute d-inline-flex align-items-center justify-content-center text-primary"
                    title="Зведення Summary (живі дані, доки місяць не збережено)"
                    style={{
                        right: "0.35rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    <CloudArrowDown size={14} aria-hidden />
                </span>
            ) : null}
            <input
                type="text"
                inputMode="decimal"
                disabled={disabled}
                title={title}
                className="form-control form-control-sm text-end"
                style={{
                    fontVariantNumeric: "tabular-nums",
                    minWidth: "5.5rem",
                    paddingRight: showLiveIcon ? "1.6rem" : undefined,
                }}
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                onFocus={() => {
                    setFocused(true);
                    if (isUsd) {
                        const v = Number(valueUah) / rate;
                        setLocal(Number.isFinite(v) ? String(Math.round(v * 100) / 100) : "0");
                    } else {
                        setLocal(String(Math.round(Number(valueUah))));
                    }
                }}
                onBlur={() => {
                    const parsed = parseMoneyInput(local);
                    setFocused(false);
                    if (isUsd) {
                        const uahCommitted = Math.round(parsed * rate);
                        if (Math.round(Number(valueUah)) !== uahCommitted) onCommit(uahCommitted);
                    } else {
                        const rounded = Math.round(parsed);
                        if (Math.round(Number(valueUah)) !== rounded) onCommit(rounded);
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        e.target.blur();
                    }
                }}
            />
        </div>
    );
}

function IntCellInput({ value, disabled, showLiveIcon, onCommit }) {
    const [local, setLocal] = useState(String(Math.round(value || 0)));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) setLocal(String(Math.round(Number(value) || 0)));
    }, [value, focused]);

    return (
        <div className="position-relative w-100">
            {showLiveIcon ? (
                <span
                    className="position-absolute d-inline-flex align-items-center justify-content-center text-primary"
                    title="З Summary"
                    style={{
                        right: "0.35rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    <CloudArrowDown size={14} aria-hidden />
                </span>
            ) : null}
            <input
                type="text"
                inputMode="numeric"
                disabled={disabled}
                className="form-control form-control-sm text-end"
                style={{
                    fontVariantNumeric: "tabular-nums",
                    minWidth: "4rem",
                    paddingRight: showLiveIcon ? "1.6rem" : undefined,
                }}
                value={local}
                onChange={(e) => setLocal(e.target.value.replace(/[^\d-]/g, ""))}
                onFocus={() => {
                    setFocused(true);
                    setLocal(String(Math.round(Number(value) || 0)));
                }}
                onBlur={() => {
                    const n = Math.max(0, Math.round(parseFloat(local) || 0));
                    setFocused(false);
                    setLocal(String(n));
                    if (Math.round(Number(value) || 0) !== n) onCommit(n);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        e.target.blur();
                    }
                }}
            />
        </div>
    );
}

function UsdCellInput({ valueUsd, disabled, showLiveIcon, onCommit }) {
    const [local, setLocal] = useState(() => formatUsd(valueUsd));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) setLocal(formatUsd(valueUsd));
    }, [valueUsd, focused]);

    return (
        <div className="position-relative w-100">
            {showLiveIcon ? (
                <span
                    className="position-absolute d-inline-flex align-items-center justify-content-center text-primary"
                    title="З Summary.daySpend (FB)"
                    style={{
                        right: "0.35rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    <CloudArrowDown size={14} aria-hidden />
                </span>
            ) : null}
            <input
                type="text"
                inputMode="decimal"
                disabled={disabled}
                className="form-control form-control-sm text-end"
                style={{
                    fontVariantNumeric: "tabular-nums",
                    minWidth: "4.5rem",
                    paddingRight: showLiveIcon ? "1.6rem" : undefined,
                }}
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                onFocus={() => {
                    setFocused(true);
                    setLocal(String(Number(valueUsd) || 0));
                }}
                onBlur={() => {
                    const parsed = parseMoneyInput(local);
                    const rounded = Math.round(parsed * 100) / 100;
                    setFocused(false);
                    setLocal(formatUsd(rounded));
                    if (Math.abs(Number(valueUsd) - rounded) > 0.001) onCommit(rounded);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        e.target.blur();
                    }
                }}
            />
        </div>
    );
}

function PctCellInput({ valuePct, disabled, onCommit }) {
    const [local, setLocal] = useState(() => (valuePct != null ? String(valuePct) : ""));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (focused) return;
        if (valuePct == null || !Number.isFinite(Number(valuePct))) {
            setLocal("");
        } else {
            setLocal(String(Math.round(Number(valuePct) * 100) / 100));
        }
    }, [valuePct, focused]);

    return (
        <input
            type="text"
            inputMode="decimal"
            disabled={disabled}
            className="form-control form-control-sm text-end"
            style={{ fontVariantNumeric: "tabular-nums", minWidth: "4rem" }}
            value={local}
            onChange={(e) => setLocal(e.target.value.replace(",", "."))}
            onFocus={() => {
                setFocused(true);
                if (valuePct != null && Number.isFinite(Number(valuePct))) {
                    setLocal(String(Math.round(Number(valuePct) * 100) / 100));
                }
            }}
            onBlur={() => {
                const parsed = parseMoneyInput(local);
                setFocused(false);
                const rounded = Math.round(parsed * 100) / 100;
                if (valuePct == null || Math.abs(Number(valuePct) - rounded) > 0.001) {
                    onCommit(rounded);
                }
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    e.target.blur();
                }
            }}
        />
    );
}

async function fetchDecompositionRange(fromYm, toYm) {
    const params = new URLSearchParams({ from: fromYm, to: toYm });
    const res = await fetch(`/api/decomposition/range?${params}`, { credentials: "include" });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Помилка завантаження");
    }
    return res.json();
}

async function postDecompositionCommit(monthsPayload) {
    const res = await fetch(`/api/decomposition/commit`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ months: monthsPayload }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Помилка збереження");
    }
    return res.json();
}

async function patchDecompositionMonthApi(yearMonth, patch) {
    const res = await fetch(`/api/decomposition/month/${encodeURIComponent(yearMonth)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patch }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Помилка збереження");
    }
    return res.json();
}

/** Іконка «з Summary»: лише для несейвнутого місяця і поля, яке ще не зафіксовано вручну в БД. */
function showLiveField(month, fieldKey) {
    if (!month || month.saved) return false;
    if (month.overriddenFields?.includes(fieldKey)) return false;
    return true;
}

const hdrStyle = { backgroundColor: "#ffd4a3" };
const subHdrStyle = { backgroundColor: "#ffe7cc" };
const scenarioColStyle = {
    position: "sticky",
    right: 0,
    zIndex: 4,
    minWidth: "7.5rem",
    maxWidth: "10rem",
    backgroundColor: "var(--bs-body-bg, #fff)",
    boxShadow: "-4px 0 8px rgba(0,0,0,0.06)",
};

export default function ReportsDecomposition() {
    const currentYear = dayjs().tz(APP_TZ).year();
    const [year, setYear] = useState(currentYear);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [patching, setPatching] = useState(false);
    const [error, setError] = useState(null);
    const [displayCurrency, setDisplayCurrency] = useState("UAH");
    const [usdSellRate, setUsdSellRate] = useState(null);
    /** Чернетка для полів поточного місяця; скидається після load (не зберігається в браузері). */
    const openMonthDraftRef = useRef({});

    const rateForDisplay =
        usdSellRate != null && Number(usdSellRate) > 0 ? Number(usdSellRate) : USD_FALLBACK;
    const moneySym = displayCurrency === "USD" ? "$" : "₴";

    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                const response = await fetch("/api/exchange-rates");
                const data = await response.json();
                const usdCurrency = Array.isArray(data) ? data.find((c) => c.currencyCodeA === 840) : null;
                if (usdCurrency?.rateSell) {
                    setUsdSellRate(usdCurrency.rateSell);
                }
            } catch (err) {
                console.error("Помилка отримання курсу валют для декомпозиції", err);
            }
        };
        fetchExchangeRates();
    }, []);

    const range = useMemo(() => {
        const fromYm = `${year}-01`;
        const nowYm = dayjs().tz(APP_TZ).format("YYYY-MM");
        const yPrefix = `${year}-`;
        const toYm =
            nowYm.startsWith(yPrefix) ? nowYm : `${year}-12`;
        return { fromYm, toYm };
    }, [year]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        openMonthDraftRef.current = {};
        try {
            const json = await fetchDecompositionRange(range.fromYm, range.toYm);
            setData(json);
        } catch (e) {
            console.error(e);
            setError(e.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, [range.fromYm, range.toYm]);

    useEffect(() => {
        clearLegacyDecompositionOpenBrowserStorage();
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const months = useMemo(() => data?.months || [], [data]);

    const scenarioKey = useMemo(() => `${year}-${months.join(",")}`, [year, months]);

    const [scenario, setScenario] = useState({
        key: "",
        baseYm: null,
        inputs: null,
        meta: null,
    });

    useEffect(() => {
        setScenario((prev) => {
            if (!data?.byMonth || !months.length) return prev;
            const openYm = dayjs().tz(APP_TZ).format("YYYY-MM");
            const ym = months.includes(openYm) ? openYm : months[months.length - 1];
            const m = data.byMonth[ym];
            if (!m?.inputs) return prev;
            if (prev.key === scenarioKey && prev.inputs) return prev;
            return {
                key: scenarioKey,
                baseYm: ym,
                inputs: { ...m.inputs },
                meta: m.meta ? { ...m.meta } : { baselineAdUah: 0, baselineAdUsd: 0 },
            };
        });
    }, [data, months, scenarioKey]);

    const updateScenarioInputs = useCallback((patch) => {
        setScenario((prev) => {
            if (!prev.inputs) return prev;
            const next = { ...prev.inputs, ...patch };
            const salesTotal = Math.max(0, Math.round(Number(next.salesTotal) || 0));
            const repeatSales = Math.min(
                Math.max(0, Math.round(Number(next.repeatSales) || 0)),
                salesTotal
            );
            return { ...prev, inputs: { ...next, salesTotal, repeatSales } };
        });
    }, []);

    const resetScenarioFromYm = useCallback(
        (ym) => {
            if (!data?.byMonth?.[ym]?.inputs) return;
            const m = data.byMonth[ym];
            setScenario((prev) => ({
                ...prev,
                baseYm: ym,
                inputs: { ...m.inputs },
                meta: m.meta ? { ...m.meta } : { baselineAdUah: 0, baselineAdUsd: 0 },
            }));
        },
        [data]
    );

    const scenarioAd = scenario.inputs ? effectiveAdUah(scenario.inputs, scenario.meta) : 0;
    const scenarioCalc = scenario.inputs ? calcDerived(scenario.inputs, scenarioAd) : null;

    const applyServerMonth = useCallback((month) => {
        setData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                byMonth: { ...prev.byMonth, [month.yearMonth]: month },
            };
        });
    }, []);

    const patchField = useCallback(async (ym, patch) => {
        const openYm = dayjs().tz(APP_TZ).format("YYYY-MM");
        if (ym === openYm) {
            openMonthDraftRef.current = { ...openMonthDraftRef.current, ...patch };
            setData((prev) => {
                if (!prev?.byMonth?.[ym]) return prev;
                const live = prev.byMonth[ym].liveSnapshot;
                if (!live) return prev;
                const month = buildOpenMonthClientView(ym, live, openMonthDraftRef.current);
                return {
                    ...prev,
                    byMonth: { ...prev.byMonth, [ym]: month },
                };
            });
            return;
        }

        setPatching(true);
        setError(null);
        try {
            const res = await patchDecompositionMonthApi(ym, patch);
            applyServerMonth(res.month);
        } catch (e) {
            console.error(e);
            setError(e.message || "Помилка збереження клітинки");
        } finally {
            setPatching(false);
        }
    }, [applyServerMonth]);

    const handleSave = useCallback(async () => {
        if (!data?.byMonth) return;
        setSaving(true);
        setError(null);
        try {
            const openYm = dayjs().tz(APP_TZ).format("YYYY-MM");
            const rows = months
                .filter((ym) => ym !== openYm)
                .map((ym) => ({
                    yearMonth: ym,
                    ...data.byMonth[ym].inputs,
                }));
            if (rows.length > 0) {
                await postDecompositionCommit(rows);
            }
            await load();
        } catch (e) {
            console.error(e);
            setError(e.message || "Помилка збереження");
        } finally {
            setSaving(false);
        }
    }, [months, data, load]);

    return (
        <div className="container-fluid py-4 px-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <div>
                    <h1 className="h3 mb-0">Декомпозиція</h1>
                </div>
                <div className="ms-lg-auto">
                    <InputGroup size="sm" className="shadow-sm" style={{ width: "auto" }}>
                        <Form.Select
                            value={year}
                            aria-label="Рік"
                            className="flex-shrink-0"
                            style={{ width: "7rem", maxWidth: "7rem" }}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </Form.Select>
                        <Button
                            type="button"
                            variant={displayCurrency === "UAH" ? "primary" : "outline-secondary"}
                            className="text-nowrap px-3"
                            onClick={() => setDisplayCurrency("UAH")}
                        >
                            ₴ UAH
                        </Button>
                        <Button
                            type="button"
                            variant={displayCurrency === "USD" ? "primary" : "outline-secondary"}
                            className="text-nowrap px-3"
                            onClick={() => setDisplayCurrency("USD")}
                        >
                            $ USD
                        </Button>
                    </InputGroup>
                </div>
            </div>

            {error ? <div className="alert alert-danger py-2">{error}</div> : null}

            {loading ? (
                <Loader />
            ) : data ? (
                <>
                    <style>
                        {`
                        .decomposition-matrix-table.table-hover tbody tr:hover > th,
                        .decomposition-matrix-table.table-hover tbody tr:hover > td {
                            background-color: var(--bs-table-hover-bg) !important;
                        }
                        `}
                    </style>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2 px-1 small">
                        <span className="text-muted">
                            Колонка «Розрахунок» лише в браузері. Обрати місяць — підставити всі поля сценарію з нього
                            (у т.ч. масштаб реклами ₴ від USD як у цього місяця):
                        </span>
                        <Form.Select
                            size="sm"
                            className="shadow-sm"
                            style={{ maxWidth: "14rem" }}
                            value={scenario.baseYm ?? months[months.length - 1] ?? ""}
                            onChange={(e) => resetScenarioFromYm(e.target.value)}
                            disabled={!months.length}
                            aria-label="Місяць-база для сценарію"
                        >
                            {months.map((ym) => (
                                <option key={ym} value={ym}>
                                    {dayjs(`${ym}-01`).format("MMMM YYYY")}
                                </option>
                            ))}
                        </Form.Select>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-secondary"
                            disabled={!scenario.baseYm}
                            onClick={() => scenario.baseYm && resetScenarioFromYm(scenario.baseYm)}
                        >
                            Скинути сценарій до бази
                        </Button>
                    </div>
                    <div className="border rounded">
                        <table
                            className="table table-sm table-bordered table-hover mb-0 align-middle decomposition-matrix-table"
                            style={{ fontSize: "0.85rem" }}
                        >
                            <thead>
                                <tr>
                                    <th
                                        className="sticky-start bg-white"
                                        style={{ position: "sticky", left: 0, zIndex: 3, minWidth: "14rem" }}
                                    >
                                        Показник
                                    </th>
                                    {months.map((ym) => {
                                        const col = data.byMonth[ym];
                                        const saved = col?.saved;
                                        const isOpen = col?.openMonth;
                                        const hasManual = !saved && !isOpen && (col?.overriddenFields?.length ?? 0) > 0;
                                        return (
                                            <th
                                                key={ym}
                                                className="text-center text-nowrap px-2"
                                                style={hdrStyle}
                                                title={
                                                    isOpen
                                                        ? "Поточний місяць: дані лише з Summary; після оновлення сторінки ручний ввід не зберігається"
                                                        : saved
                                                          ? "Місяць збережений кнопкою «Зберегти» — лише з DecompositionMonth"
                                                          : "Поля без ручних правок оновлюються з Summary; правки в DecompositionMonth"
                                                }
                                            >
                                                <div className="d-flex flex-column align-items-center gap-1">
                                                    <span className="text-nowrap">
                                                        {dayjs(`${ym}-01`).format("MMMM YY")}
                                                    </span>
                                                    {saved ? (
                                                        <span className="badge bg-secondary rounded-pill">
                                                            <Lock size={10} className="me-1" aria-hidden />
                                                            збережено
                                                        </span>
                                                    ) : isOpen ? (
                                                        <span className="badge bg-info text-dark rounded-pill">
                                                            Summary
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <span className="badge bg-info text-dark rounded-pill">
                                                                live + Summary
                                                            </span>
                                                            {hasManual ? (
                                                                <span className="badge bg-warning text-dark rounded-pill">
                                                                    є ручні
                                                                </span>
                                                            ) : null}
                                                        </>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th
                                        className="text-center px-1 align-top"
                                        style={{ ...hdrStyle, ...scenarioColStyle }}
                                        title="Лише в браузері: змінюйте поля — інші показники перераховуються. Масштаб реклами в ₴ як у місяця, обраного в селекторі під таблицею."
                                    >
                                        <div className="d-flex flex-column align-items-center gap-1">
                                            <span className="text-wrap">Розрахунок</span>
                                            <span className="badge bg-secondary rounded-pill">сценарій</span>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td colSpan={months.length + 2} className="fw-semibold small py-2" style={subHdrStyle}>
                                        Дохід і маржа (
                                        {displayCurrency === "USD"
                                            ? "відображення в $ за НБУ sell, у БД лишаються ₴"
                                            : "₴ з KeyCRM"}
                                        )
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        ТО, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.turnoverUah ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <MoneyCellInput
                                                    valueUah={v}
                                                    displayCurrency={displayCurrency}
                                                    usdSellRate={rateForDisplay}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "turnoverUah")}
                                                    onCommit={(n) => patchField(ym, { turnoverUah: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <MoneyCellInput
                                                valueUah={scenario.inputs.turnoverUah ?? 0}
                                                displayCurrency={displayCurrency}
                                                usdSellRate={rateForDisplay}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ turnoverUah: roundUah(n) })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Середній чек, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatMoneyCell(c.avgCheckUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs && scenarioCalc ? (
                                            Math.max(0, Math.round(Number(scenario.inputs.salesTotal) || 0)) > 0 ? (
                                                <MoneyCellInput
                                                    valueUah={scenarioCalc.avgCheckUah ?? 0}
                                                    displayCurrency={displayCurrency}
                                                    usdSellRate={rateForDisplay}
                                                    disabled={saving || patching}
                                                    showLiveIcon={false}
                                                    title="Перераховує ТО = середній чек × кількість продажів"
                                                    onCommit={(n) => {
                                                        const sales = Math.max(
                                                            0,
                                                            Math.round(Number(scenario.inputs.salesTotal) || 0)
                                                        );
                                                        if (sales <= 0) return;
                                                        updateScenarioInputs({
                                                            turnoverUah: roundUah((Number(n) || 0) * sales),
                                                        });
                                                    }}
                                                />
                                            ) : (
                                                <span className="text-muted small">0 продажів</span>
                                            )
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Маржа всього, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.marginUah ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <MoneyCellInput
                                                    valueUah={v}
                                                    displayCurrency={displayCurrency}
                                                    usdSellRate={rateForDisplay}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "marginUah")}
                                                    onCommit={(n) => patchField(ym, { marginUah: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <MoneyCellInput
                                                valueUah={scenario.inputs.marginUah ?? 0}
                                                displayCurrency={displayCurrency}
                                                usdSellRate={rateForDisplay}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ marginUah: roundUah(n) })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Маржинальність, %
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatPct(c.marginPctOfTurnover)}
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs && scenarioCalc ? (
                                            (Number(scenario.inputs.turnoverUah) || 0) > 0 ? (
                                                <PctCellInput
                                                    valuePct={scenarioCalc.marginPctOfTurnover}
                                                    disabled={saving || patching}
                                                    onCommit={(pct) => {
                                                        const t = Number(scenario.inputs.turnoverUah) || 0;
                                                        updateScenarioInputs({
                                                            marginUah: roundUah(t * ((Number(pct) || 0) / 100)),
                                                        });
                                                    }}
                                                />
                                            ) : (
                                                <span className="text-muted small" title="Щоб задати %, спочатку вкажіть ТО &gt; 0">
                                                    —
                                                </span>
                                            )
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Маржа на 1 продаж, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatMoneyCell(c.marginPerUnitUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc
                                            ? formatMoneyCell(
                                                  scenarioCalc.marginPerUnitUah,
                                                  displayCurrency,
                                                  rateForDisplay
                                              )
                                            : "—"}
                                    </td>
                                </tr>

                                <tr>
                                    <td colSpan={months.length + 2} className="fw-semibold small py-2" style={subHdrStyle}>
                                        Продажі та заявки
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Заявок (leads)
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.leads ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <IntCellInput
                                                    value={v}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "leads")}
                                                    onCommit={(n) => patchField(ym, { leads: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <IntCellInput
                                                value={scenario.inputs.leads ?? 0}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ leads: n })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Продажі всього, шт
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.salesTotal ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <IntCellInput
                                                    value={v}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "salesTotal")}
                                                    onCommit={(n) => patchField(ym, { salesTotal: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <IntCellInput
                                                value={scenario.inputs.salesTotal ?? 0}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ salesTotal: n })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Продажі повторних, шт
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.repeatSales ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <IntCellInput
                                                    value={v}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "repeatSales")}
                                                    onCommit={(n) => patchField(ym, { repeatSales: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <IntCellInput
                                                value={scenario.inputs.repeatSales ?? 0}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ repeatSales: n })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        % повторних продажів
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatPct(c.pctRepeat)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc ? formatPct(scenarioCalc.pctRepeat) : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Товарів / послуг, шт
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.goodsQty ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <IntCellInput
                                                    value={v}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "goodsQty")}
                                                    onCommit={(n) => patchField(ym, { goodsQty: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <IntCellInput
                                                value={scenario.inputs.goodsQty ?? 0}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ goodsQty: n })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Од. у чеку
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatRatio(c.unitsPerCheck)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc ? formatRatio(scenarioCalc.unitsPerCheck) : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Конверсія у продаж, %
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatPct(c.conversionPct)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc ? formatPct(scenarioCalc.conversionPct) : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Менеджерів
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.managers ?? 2;
                                        return (
                                            <td key={ym} className="p-1">
                                                <IntCellInput
                                                    value={v}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "managers")}
                                                    onCommit={(n) => patchField(ym, { managers: Math.max(1, n) })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <IntCellInput
                                                value={scenario.inputs.managers ?? 2}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ managers: Math.max(1, n) })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Заявок на менеджера
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatRatio(c.leadsPerManager)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc ? formatRatio(scenarioCalc.leadsPerManager) : "—"}
                                    </td>
                                </tr>

                                <tr>
                                    <td colSpan={months.length + 2} className="fw-semibold small py-2" style={subHdrStyle}>
                                        Маркетинг (поле реклами — USD з FB; решта грошових колонок у вибраній валюті)
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Рекламний бюджет, USD
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.adBudgetUsd ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <UsdCellInput
                                                    valueUsd={v}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "adBudgetUsd")}
                                                    onCommit={(n) => patchField(ym, { adBudgetUsd: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <UsdCellInput
                                                valueUsd={scenario.inputs.adBudgetUsd ?? 0}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ adBudgetUsd: n })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Вартість заявки, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatMoneyCell(c.costPerLeadUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc
                                            ? formatMoneyCell(
                                                  scenarioCalc.costPerLeadUah,
                                                  displayCurrency,
                                                  rateForDisplay
                                              )
                                            : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Ціна клієнта нового, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatMoneyCell(c.newCustomerCacUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc
                                            ? formatMoneyCell(
                                                  scenarioCalc.newCustomerCacUah,
                                                  displayCurrency,
                                                  rateForDisplay
                                              )
                                            : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Ціна клієнта, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatMoneyCell(c.customerCacUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc
                                            ? formatMoneyCell(
                                                  scenarioCalc.customerCacUah,
                                                  displayCurrency,
                                                  rateForDisplay
                                              )
                                            : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Прибуток з 1-го, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatMoneyCell(c.profitPerSaleUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc
                                            ? formatMoneyCell(
                                                  scenarioCalc.profitPerSaleUah,
                                                  displayCurrency,
                                                  rateForDisplay
                                              )
                                            : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Прибуток всього, {moneySym}{" "}
                                        <span className="text-muted">
                                            {displayCurrency === "USD"
                                                ? "(розрахунок у ₴, показ $)"
                                                : "(маржа − реклама ₴)"}
                                        </span>
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end small pe-2 fw-medium" style={hdrStyle}>
                                                {formatMoneyCell(c.totalProfitUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td
                                        className="text-end small pe-2 fw-medium"
                                        style={{ ...hdrStyle, ...scenarioColStyle, fontVariantNumeric: "tabular-nums" }}
                                    >
                                        {scenarioCalc
                                            ? formatMoneyCell(
                                                  scenarioCalc.totalProfitUah,
                                                  displayCurrency,
                                                  rateForDisplay
                                              )
                                            : "—"}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="sticky-start bg-light" style={{ position: "sticky", left: 0, zIndex: 2 }}>
                                        Окупність (маржа / реклама; безрозмірна)
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td key={ym} className="text-end text-muted small pe-2">
                                                {formatRatio(c.roi)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end text-muted small pe-2" style={scenarioColStyle}>
                                        {scenarioCalc ? formatRatio(scenarioCalc.roi) : "—"}
                                    </td>
                                </tr>

                                <tr>
                                    <td colSpan={months.length + 2} className="fw-semibold small py-2" style={subHdrStyle}>
                                        Витрати після прибутку та «в кишеню»
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        ФОП / зарплати, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.payrollUah ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <MoneyCellInput
                                                    valueUah={v}
                                                    displayCurrency={displayCurrency}
                                                    usdSellRate={rateForDisplay}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "payrollUah")}
                                                    onCommit={(n) => patchField(ym, { payrollUah: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <MoneyCellInput
                                                valueUah={scenario.inputs.payrollUah ?? 0}
                                                displayCurrency={displayCurrency}
                                                usdSellRate={rateForDisplay}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ payrollUah: roundUah(n) })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Інвестиції з прибутку, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.reinvestmentUah ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <MoneyCellInput
                                                    valueUah={v}
                                                    displayCurrency={displayCurrency}
                                                    usdSellRate={rateForDisplay}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "reinvestmentUah")}
                                                    onCommit={(n) => patchField(ym, { reinvestmentUah: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <MoneyCellInput
                                                valueUah={scenario.inputs.reinvestmentUah ?? 0}
                                                displayCurrency={displayCurrency}
                                                usdSellRate={rateForDisplay}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ reinvestmentUah: roundUah(n) })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start bg-white fw-medium"
                                        style={{ position: "sticky", left: 0, zIndex: 2 }}
                                    >
                                        Все інше, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const month = data.byMonth[ym];
                                        const v = month?.inputs?.otherUah ?? 0;
                                        return (
                                            <td key={ym} className="p-1">
                                                <MoneyCellInput
                                                    valueUah={v}
                                                    displayCurrency={displayCurrency}
                                                    usdSellRate={rateForDisplay}
                                                    disabled={saving || patching}
                                                    showLiveIcon={showLiveField(month, "otherUah")}
                                                    onCommit={(n) => patchField(ym, { otherUah: n })}
                                                />
                                            </td>
                                        );
                                    })}
                                    <td className="p-1" style={scenarioColStyle}>
                                        {scenario.inputs ? (
                                            <MoneyCellInput
                                                valueUah={scenario.inputs.otherUah ?? 0}
                                                displayCurrency={displayCurrency}
                                                usdSellRate={rateForDisplay}
                                                disabled={saving || patching}
                                                showLiveIcon={false}
                                                onCommit={(n) => updateScenarioInputs({ otherUah: roundUah(n) })}
                                            />
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        className="sticky-start fw-semibold"
                                        style={{ position: "sticky", left: 0, zIndex: 2, ...hdrStyle }}
                                    >
                                        Забрав в кишеню, {moneySym}
                                    </td>
                                    {months.map((ym) => {
                                        const ad = effectiveAdUah(data.byMonth[ym].inputs, data.byMonth[ym].meta);
                                        const c = calcDerived(data.byMonth[ym].inputs, ad);
                                        return (
                                            <td
                                                key={ym}
                                                className="text-end pe-2 fw-semibold"
                                                style={{ ...hdrStyle, fontVariantNumeric: "tabular-nums" }}
                                            >
                                                {formatMoneyCell(c.netPocketUah, displayCurrency, rateForDisplay)}
                                            </td>
                                        );
                                    })}
                                    <td
                                        className="text-end pe-2 fw-semibold"
                                        style={{
                                            ...hdrStyle,
                                            ...scenarioColStyle,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {scenarioCalc
                                            ? formatMoneyCell(
                                                  scenarioCalc.netPocketUah,
                                                  displayCurrency,
                                                  rateForDisplay
                                              )
                                            : "—"}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-3">
                        <Button variant="primary" disabled={saving || patching || loading} onClick={handleSave}>
                            {saving ? "Збереження…" : "Зберегти всі місяці з таблиці"}
                        </Button>
                    </div>
                </>
            ) : (
                <p className="text-muted mb-0">Не вдалося завантажити дані.</p>
            )}
        </div>
    );
}
